
import { Component, Vue, Prop, Watch, Inject } from 'vue-property-decorator';
import { CreateElement, VNode } from 'vue';
import { Validator } from 'vee-validate';
import { observeDom } from './../utils/dom-observer';
import TabButtonHelper from './tab-button-helper.vue';
import Tab from './tab.vue';

@Component
export default class Tabs extends Vue {
    @Inject('$validator') public $validator!: Validator;
    @Prop({ type: String, default: 'div' }) public tag!: string;
    @Prop({ type: Boolean, default: false }) public card!: boolean;
    @Prop({ type: Boolean, default: false }) public small!: boolean;
    @Prop({ type: Number, required: true}) public value!: number;
    @Prop({ type: Boolean, default: false }) public pills!: boolean;
    @Prop({ type: Boolean, default: false }) public vertical!: boolean;
    @Prop({ type: Boolean, default: false }) public bottom!: boolean;
    @Prop({ type: Boolean, default: false }) public end!: boolean;
    @Prop({ type: Boolean, default: false }) public noFade!: boolean;
    @Prop({ type: Boolean, default: false }) public noNavStyle!: boolean;
    @Prop({ type: Boolean, default: false }) public noKeyNav!: boolean;
    @Prop({ type: Boolean, default: false }) public lazy!: boolean;
    @Prop({ type: [String, Array, Object], default: null }) public contentClass!: string | Array<string> | object;
    @Prop({ type: [String, Array, Object], default: null }) public navClass!: string | Array<string> | object;
    @Prop({ type: [String, Array, Object], default: null }) public navWrapperClass!: string | Array<string> | object;

    private get fade(): boolean {
        return !this.noFade;
    }
    private get navStyle(): string {
        return this.pills ? 'pills' : 'tabs';
    }

    private currentTab: number = this.value;
    private tabs: Array<Tab> = new Array<Tab>();

    @Watch('currentTab')
    private onTabChanged(newValue: number, oldValue: number): void {
        if (newValue === oldValue) {
            return;
        }

        this.$root.$emit('changed::tab', this, newValue, this.tabs[newValue]);
        this.$emit('input', newValue);
        this.tabs[newValue].$emit('click');
    }
    @Watch('value')
    private onValueChanged(newValue: number, oldValue: number): void {
        if (newValue === oldValue) {
            return;
        }

        if (typeof oldValue !== 'number') {
            oldValue = 0;
        }

        const direction = newValue < oldValue ? -1 : 1;
        this.setTab(newValue, false, direction);
    }
    private onKeyNavigation(event: KeyboardEvent): void {
        if (this.noKeyNav) {
            return;
        }

        const key = event.keyCode;
        const shift = event.shiftKey;

        function stop() {
            event.preventDefault();
            event.stopPropagation();
        }

        // Up or Left
        if (key === 38 || key === 38) {
            stop();

            if (shift) {
                this.setTab(0, false, 1);
            } else {
                this.previousTab();
            }
        // Down or Right
        } else if (key === 40 || key === 39) {
            stop();

            if (shift) {
                this.setTab(this.tabs.length - 1, false, -1);
            } else {
                this.nextTab();
            }
        }
    }
    private nextTab(): void {
        this.setTab(this.currentTab + 1, false, 1);
    }
    private previousTab(): void {
        this.setTab(this.currentTab - 1, false, -1);
    }
    private setTab(index: number, force: boolean, direction: number = 0): void {
        direction = direction === 0 ? 0 : direction > 0 ? 1 : -1;

        if (!force && index === this.currentTab) {
            return;
        }

        const step = this.tabs[index];

        if (!step) {
            // NOTE: This resets the v-model current step.
            this.$emit('input', this.currentTab);
            return;
        }

        if (step.disabled) {
            if (direction) {
                this.setTab(index + direction, force, direction);
            }

            return;
        }

        // Activate requested current step, and deactivte any old steps
        this.tabs.forEach((s) => {
            if (step === s) {
                this.$set(s, 'localActive', true);
            } else {
                this.$set(s, 'localActive', false);
            }
        });

        this.currentTab = index;
    }
    private updateTabs(): void {
        this.tabs = this.$slots!.default!.filter((node: VNode) => node && node.componentInstance && (node.componentInstance as Tab).isTab)
                                       .map((node: VNode) => node.componentInstance as Tab);

        let tabIndex: number | null = null;

        this.tabs.forEach((tab, index) => {
            if (tab.localActive && !tab.disabled) {
                tabIndex = index;
            }
        });

        if (tabIndex === null) {
            if (this.currentTab >= this.tabs.length) {
                this.setTab(this.tabs.length - 1, true, -1);
                return;
            } else if (this.tabs[this.currentTab] && !this.tabs[this.currentTab].disabled) {
                tabIndex = this.currentTab;
            }
        }

        if (tabIndex === null) {
            this.tabs.forEach((tab, index) => {
                if (!tab.disabled && tabIndex === null) {
                    tabIndex = index;
                }
            });
        }

        this.setTab(tabIndex || 0, true, 0);
    }

    public render(h: CreateElement): VNode {
        const tabs = this.tabs;

        // Navigation 'buttons'
        const buttons = tabs.map((tab, index) => {
            return h(TabButtonHelper, {
                key: index,
                props: {
                    content: tab.$slots.title || tab.title,
                    href: tab.href,
                    id: tab.controlledBy || '',
                    active: tab.localActive,
                    disabled: tab.disabled,
                    setSize: tabs.length,
                    posInSet: index + 1,
                    controls: '',
                    linkClass: tab.titleLinkClass,
                    itemClass: tab.titleItemClass,
                    noKeyNav: this.noKeyNav,
                },
                on: {
                    click: (event: Event) => {
                        this.setTab(index, false);
                    },
                },
            });
        });

        // Navigation 'button' wrapper
        let navs = h('ul',
        {
            class: [
                'nav', {
                    [`nav-${this.navStyle}`]: !this.noNavStyle,
                    [`card-header-${this.navStyle}`]: this.card && !this.vertical,
                    'card-header': this.card && this.vertical,
                    'h-100': this.card && this.vertical,
                    'flex-column': this.vertical,
                    'border-bottom-0': this.vertical,
                    'rounded-0': this.vertical,
                    'small': this.small,
                },
                this.navClass,
            ],
            attrs: {
                role: 'tablist',
                tabindex: this.noKeyNav ? null : '0',
                id: '',
            },
            on: { keydown: this.onKeyNavigation },
        },
        [buttons, this.$slots.tabs]);

        navs = h('div', {
            class: [{
                'card-header': this.card && !this.vertical && !(this.end || this.bottom),
                'card-footer': this.card && !this.vertical && (this.end || this.bottom),
                'col-auto': this.vertical,
            }, this.navWrapperClass],
        },
        [navs]);

        let empty: VNode | null = null;

        if (tabs && tabs.length) {
            // @ts-ignore
            empty = h(false);
        } else {
            empty = h('div', { class: [ 'tab-pane', 'active', { 'card-body': this.card }]}, this.$slots.empty);
        }

        // Main content
        const content = h('div', {
            ref: 'tabsContainer',
            class: ['tab-content', { col: this.vertical }, this.contentClass],
            attrs: { id: '' },
        }, [this.$slots.default, empty!]);

        return h(this.tag, {
            class: [ 'tabs', { 'row': this.vertical, 'no-gutters': this.vertical && this.card }],
            attrs: { id: '' },
        },
        // @ts-ignore
        [this.end || this.bottom ? content : h(false), [navs], this.end || this.bottom ? h(false) : content]);
    }
    public mounted(): void {
        this.updateTabs();

        observeDom(this.$refs.tabsContainer as Element | Vue | null, this.updateTabs.bind(this), { subtree: false });
    }
}
